import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import { MdArrowOutward, MdDeleteForever } from "react-icons/md";
import { FaEdit, FaEye } from "react-icons/fa";
import { useContext, useEffect, useState } from "react";
import nodata from "../../../assets/img/No_image.png";
import NoCatalogue from "../../../assets/img/List_Is_Empty.svg";
import NoProduct from "../../../assets/img/NoProductFound.svg";
import { Table, useTable } from "ka-table";
import {
  DataType,
  EditingMode,
  PagingPosition,
  SortingMode,
  FilteringMode,
} from "ka-table/enums";
import "ka-table/style.css";
import { AuthContext } from "../../../Provider/AuthProvider";
import Footer from "../../../components/footer/Footer";
import AdminNavbar from "../../Components/AdminNavbar/AdminNavbar";
import CatalogueTable from "../../Components/CatalogueTable/CatalogueTable";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";

const Dashboard = () => {
  const { user } = useContext(AuthContext);
  // console.log("Logged in user:", user);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // ✅ SweetAlert2 success popup
  const showSuccessAlert = (title, text = "") =>
    Swal.fire({
      title,
      text,
      icon: "success",
      confirmButtonText: "OK",
      customClass: {
        confirmButton: "swal-confirm-button-success",
      },
      buttonsStyling: false,
    });

  useEffect(() => {
    fetch("https://backend-server-nine-opal.vercel.app/contents", {
      // credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        setCards(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error loading data:", err);
        setLoading(false);
      });
  }, []);

  // Detect screen resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [selectedCategory, setSelectedCategory] =
    useState("Search by Category");
  const [selectedBrand, setSelectedBrand] = useState("Search by Brand");

  const categories = [...new Set(cards.map((card) => card.product_Category))];
  const brands = [...new Set(cards.map((card) => card.brand_Name))];
  const productNames = [...new Set(cards.map((card) => card.Product_name))];
  const catalogues = [...new Set(cards.map((card) => card.catalogue_Name))];

  const filteredCards = cards.filter((card) => {
    const categoryMatch =
      selectedCategory === "Search by Category" ||
      card.product_Category === selectedCategory;
    const brandMatch =
      selectedBrand === "Search by Brand" || card.brand_Name === selectedBrand;
    return categoryMatch && brandMatch;
  });

  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        fetch(`https://backend-server-nine-opal.vercel.app/contents/${id}`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          // credentials: "include",
          body: JSON.stringify({ id }),
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.deletedCount > 0) {
              showSuccessAlert("Deleted!", "Your file has been deleted.");
              setCards((prev) => prev.filter((c) => c._id !== id));
            }
          });
      }
    });
  };

  const tableInstance = useTable();
  const kaTableData = filteredCards.map((card, index) => ({
    id: index + 1,
    _id: card._id,
    name: card.Product_name,
    brand: card.brand_Name,
    size: card.Pack_Size,
    category: card.product_Category,
  }));

  const columns = [
    {
      key: "id",
      title: "Number",
      dataType: DataType.Number,
      isEditable: false,
      isFilterable: false,
    },
    {
      key: "name",
      title: "Name",
      dataType: DataType.String,
      headerFilterData: productNames,
    },
    {
      key: "brand",
      title: "Brand Name",
      dataType: DataType.String,
      headerFilterData: brands,
    },
    {
      key: "size",
      title: "Product Size",
      dataType: DataType.String,
      isFilterable: false,
    },
    {
      key: "category",
      title: "Category",
      dataType: DataType.String,
      headerFilterData: categories,
    },
    {
      key: "actions",
      title: "Actions",
      isEditable: false,
      isFilterable: false,
    },
  ];

  if (loading) {
    return (
      <div className="h-screen flex justify-center items-center">
        <p className="text-xl">Loading product list...</p>
      </div>
    );
  }

  return (
    <div>
      <AdminNavbar />
      <div className="container mx-auto mb-6">
        <div
          className="hero min-h-80 rounded-2xl"
          style={{
            backgroundImage: `url(https://www.eurofoodsgroup.co.uk/wp-content/uploads/2017/02/Unknown-2.jpeg)`,
          }}
        ></div>

        {user && (
          <div className="flex gap-4 lg:px-12 px-4 pt-6 pb-10">
            <div className="avatar online">
              <div className="lg:w-24 w-16 rounded-full">
                <img src={user?.photoURL || nodata} loading="lazy" alt="User" />
              </div>
            </div>
            <div className="text-left">
              <h1 className="lg:text-2xl font-bold font-mono">
                {user?.displayName || "No Name"}
              </h1>
              <p className="lg:text-xl text-xs">{user?.email || "No Email"}</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex justify-between items-center flex-wrap lg:px-10 px-4 gap-4">
          <h1 className="md:text-3xl text-xl font-head text-[#080808] font-bold">
            Product list: {filteredCards.length}
          </h1>
          <div className="flex flex-wrap items-center gap-4">
            {/* Brand select */}
            <select
              className="select select-bordered select-sm w-full sm:w-auto text-sm order-2 sm:order-1 lg:order-1"
              value={selectedBrand}
              onChange={(e) => setSelectedBrand(e.target.value)}
            >
              <option disabled>Search by Brand</option>
              <option>Search by Brand</option>
              {brands.map((brand, i) => (
                <option key={i} value={brand}>
                  {brand}
                </option>
              ))}
            </select>

            {/* Category select */}
            <select
              className="select select-bordered select-sm w-full sm:w-auto text-sm order-3 sm:order-2 lg:order-2"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option disabled>Search by Category</option>
              <option>Search by Category</option>
              {categories.map((cat, i) => (
                <option key={i} value={cat}>
                  {cat}
                </option>
              ))}
            </select>

            {/* Add New Data button */}
            <Link
              to="/dashboard/addNewProduct"
              className="order-1 sm:order-3 lg:order-3 w-full sm:w-auto"
            >
              <button className="w-full sm:w-auto whitespace-nowrap flex justify-center items-center px-4 py-2 rounded-lg font-semibold text-sm lg:text-base border-none text-white bg-[#080808] hover:bg-[#f0ca4e] hover:text-black">
                Add New Data <MdArrowOutward className="ml-1 text-lg" />
              </button>
            </Link>
          </div>
        </div>

        {filteredCards.length === 0 ? (
          <div className="flex flex-col items-center justify-center mt-10">
            <img
              src={NoProduct}
              loading="lazy"
              alt="No Data"
              className="w-64"
            />
            <p className="text-xl text-gray-600 mt-4">
              No Product available. Add some Product to see them here!
            </p>
          </div>
        ) : isMobile ? (
          /* Mobile Card Layout */
          <div className="grid grid-cols-1 gap-4 px-4 mt-6">
            {filteredCards.map((card) => (
              <div
                key={card._id}
                className="border justify-center rounded-lg p-4 shadow-sm bg-white"
              >
                <h2 className="font-bold text-lg">{card.Product_name}</h2>
                <p className="text-sm text-gray-600">
                  Brand: {card.brand_Name}
                </p>
                <p className="text-sm text-gray-600">Size: {card.Pack_Size}</p>
                <p className="text-sm text-gray-600">
                  Category: {card.product_Category}
                </p>
                <div className="flex gap-2 mt-3">
                  <Link to={`viewProduct/${card._id}`}>
                    <button className="btn btn-sm bg-blue-500 text-white hover:bg-blue-600">
                      <FaEye />
                    </button>
                  </Link>
                  <Link to={`editProduct/${card._id}`}>
                    <button className="btn btn-sm bg-green-500 text-white hover:bg-green-600">
                      <FaEdit />
                    </button>
                  </Link>
                  <button
                    onClick={() => handleDelete(card._id)}
                    className="btn btn-sm bg-red-500 text-white hover:bg-red-600"
                  >
                    <MdDeleteForever />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Desktop Table Layout */
          <div className="lg:px-10 px-4 mt-6 overflow-x-auto">
            <div className="min-w-[800px]">
              <Table
                className="w-full text-sm"
                table={tableInstance}
                columns={columns}
                data={kaTableData}
                rowKeyField="_id"
                paging={{
                  enabled: true,
                  pageIndex: 0,
                  pageSize: 10,
                  pageSizes: [5, 10, 15],
                  position: PagingPosition.Bottom,
                }}
                sortingMode={SortingMode.Single}
                editingMode={EditingMode.None}
                filteringMode={FilteringMode.HeaderFilter}
                childComponents={{
                  cellText: {
                    content: ({ column, rowData }) => {
                      if (column.key === "actions") {
                        return (
                          <div className="flex justify-center gap-2">
                            <Link to={`viewProduct/${rowData._id}`}>
                              <button className="btn btn-xs bg-blue-500 text-white hover:bg-blue-600">
                                <FaEye />
                              </button>
                            </Link>
                            <Link to={`editProduct/${rowData._id}`}>
                              <button className="btn btn-xs bg-green-500 text-white hover:bg-green-600">
                                <FaEdit />
                              </button>
                            </Link>
                            <button
                              onClick={() => handleDelete(rowData._id)}
                              className="btn btn-xs bg-red-500 text-white hover:bg-red-600"
                            >
                              <MdDeleteForever />
                            </button>
                          </div>
                        );
                      }
                    },
                  },
                  pagingPages: {
                    content: ({ pagesCount, dispatch, pageIndex }) => {
                      const pages = [];
                      for (let i = 0; i < pagesCount; i++) {
                        pages.push(
                          <button
                            key={i}
                            onClick={() =>
                              dispatch({
                                type: "PageIndexChanged",
                                pageIndex: i,
                              })
                            }
                            className={`px-3 py-1 mx-1 rounded ${
                              i === pageIndex
                                ? "bg-black text-white"
                                : "bg-gray-200 hover:bg-gray-300"
                            }`}
                          >
                            {i + 1}
                          </button>
                        );
                      }

                      return (
                        <div className="flex items-center justify-center mt-4 space-x-2">
                          {/* Previous */}
                          <button
                            onClick={() =>
                              pageIndex > 0 &&
                              dispatch({
                                type: "PageIndexChanged",
                                pageIndex: pageIndex - 1,
                              })
                            }
                            disabled={pageIndex === 0}
                            className="px-3 flex items-center  py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
                          >
                            <IoIosArrowBack />
                            Previous
                          </button>

                          {pages}

                          {/* Next */}
                          <button
                            onClick={() =>
                              pageIndex < pagesCount - 1 &&
                              dispatch({
                                type: "PageIndexChanged",
                                pageIndex: pageIndex + 1,
                              })
                            }
                            disabled={pageIndex >= pagesCount - 1}
                            className="px-3 py-1 flex items-center rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
                          >
                            Next
                            <IoIosArrowForward />
                          </button>
                        </div>
                      );
                    },
                  },
                }}
              />
            </div>
          </div>
        )}
        {/* Catalogue Table Section */}
        <div className="lg:px-10 px-4">
          {filteredCards.length === 0 ? (
            <div className="flex flex-col items-center justify-center mt-10">
              <img
                src={NoCatalogue}
                loading="lazy"
                alt="No Catalogue"
                className="w-64"
              />
              <p className="text-xl text-gray-600 mt-4">
                No Catalogue available. Add some Catalogue to see them here!
              </p>
            </div>
          ) : (
            <CatalogueTable />
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Dashboard;
